import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { SectionHeader } from '~/features/experiments/ab_plp_redesign_2025/components/section-header';
import { ChartUpIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

const RESULTS_GRADIENT = 'linear-gradient(69.83deg, #031643 0%, #00298C 50%, #6100B0 100%)';
const STRIPES_SRC = '/images/experiments/ab_plp_redesign_2025/diagonal-lines.svg';

const VALUE_CARDS = [
  {
    icon: '/images/about-us/future/value-outcome.svg',
    iconBg: '#00298C',
    iconRadius: '6px',
    title: 'Be outcome obsessed',
    body: 'Center our actions around our customers and learners. Derive motivation from and be guided by measurable outcomes.',
    cardBg: '#F4F8FF',
    cardBorder: '#D2DFFF',
    divider: '#AFC6FF',
  },
  {
    icon: '/images/about-us/future/value-curiosity.svg',
    iconBg: '#6100B0',
    iconRadius: '4px',
    title: 'Embrace curiosity',
    body: 'Champion ongoing learning, challenge norms, and encourage healthy debate to foster a workplace of innovation and exploration.',
    cardBg: '#FDF8FF',
    cardBorder: '#F6DFFF',
    divider: '#EBBAFF',
  },
  {
    icon: '/images/about-us/future/value-lead.svg',
    iconBg: '#6100B0',
    iconRadius: '4px',
    title: 'Take the lead',
    body: 'Give the power to ourselves and others to speak up, take data-driven risks, innovate, and embrace change.',
    cardBg: '#FDF8FF',
    cardBorder: '#F6DFFF',
    divider: '#EBBAFF',
  },
  {
    icon: '/images/about-us/future/value-assist.svg',
    iconBg: '#00298C',
    iconRadius: '6px',
    title: 'Celebrate the assist',
    body: 'Collaborate with compassion, respect, and trust. Celebrate the unseen work that makes victories possible.',
    cardBg: '#F4F8FF',
    cardBorder: '#D2DFFF',
    divider: '#AFC6FF',
  },
] as const;

const RESULTS_COPY =
  "Udacity works with individuals, organizations, and governments interested in upskilling for what's next. In doing so, we drive favorable career outcomes for individual learners, scaled digital transformation for organizations, and meaningful economic change for governments around the globe. And what's more, Udacity's scholarships ensure that those we don't reach through these channels still have a chance to benefit from Udacity's programs.";

type ResultStat = {
  value: string;
  valueColor: string;
  label: string;
  sub: string;
  subColor: string;
  prefix?: string;
  suffix?: string;
};

const RESULT_STATS: ResultStat[] = [
  {
    value: '97',
    valueColor: '#7BA2FF',
    label: 'Nanodegree programs',
    sub: '& individual courses',
    subColor: '#7BA2FF',
  },
  {
    value: '15k+',
    valueColor: '#E5A5FF',
    label: 'hours of catalog content',
    sub: '(updating weekly!)',
    subColor: '#E5A5FF',
  },
  {
    value: '700+',
    valueColor: '#FFB27D',
    label: 'industry experts',
    sub: 'in our mentor network',
    subColor: '#FFB27D',
  },
  {
    prefix: '$',
    value: '2.9',
    suffix: 'M',
    valueColor: '#89E6FF',
    label: 'potential cost savings',
    sub: 'from increased productivity',
    subColor: '#89E6FF',
  },
];

export function FutureSection() {
  return (
    <Box as='section' bg='grey.50' py={{ base: 12, md: '7.5rem' }}>
      <Flex align='center' direction='column' gap={{ base: 10, md: '4rem' }} maxW='1056px' mx='auto'>
        <Flex align='center' direction='column' gap={6} maxW='836px' px={{ base: 4, md: 0 }}>
          <Heading
            as='h2'
            color='#0A0B0F'
            fontFamily='heading'
            fontSize={{ base: '2rem', md: '3.375rem' }}
            fontWeight={600}
            letterSpacing='-0.02em'
            lineHeight={1.1}
            textAlign='center'
          >
            At Udacity, we are forging futures in tech
          </Heading>
          <Text color='#5A5C62' fontFamily='body' fontSize='1rem' lineHeight={1.5} textAlign='center'>
            We are delivering impactful and cutting edge tech education that enables individuals and organizations to
            unlock their potential.
          </Text>
        </Flex>

        <Flex flexWrap='wrap' gap={{ base: 6, md: 8 }} px={{ base: 4, md: 0 }} width='full'>
          {VALUE_CARDS.map((card) => (
            <Flex
              key={card.title}
              bg={card.cardBg}
              border={`1px solid ${card.cardBorder}`}
              borderRadius='8px'
              direction='column'
              flex={{ base: '1 1 100%', md: '1 1 calc(50% - 16px)' }}
              gap={5}
              p={{ base: 6, md: 8 }}
            >
              <Flex align='center' borderBottom={`1px solid ${card.divider}`} gap={5} pb={6}>
                <Flex
                  align='center'
                  bg={card.iconBg}
                  borderRadius={card.iconRadius}
                  height='40px'
                  justify='center'
                  overflow='hidden'
                  shrink={0}
                  width='40px'
                >
                  <Image alt='' height={26} src={card.icon} style={{ objectFit: 'contain' }} width={26} />
                </Flex>
                <Heading
                  as='h3'
                  color='#0A0B0F'
                  fontFamily='heading'
                  fontSize='1.5rem'
                  fontWeight={600}
                  letterSpacing='-0.01em'
                  lineHeight={1.15}
                >
                  {card.title}
                </Heading>
              </Flex>
              <Text color='#25262A' fontFamily='body' fontSize='1rem' lineHeight={1.5}>
                {card.body}
              </Text>
            </Flex>
          ))}
        </Flex>

        <Box
          borderRadius={{ base: 0, md: '12px' }}
          mb={{ base: -10, md: 0 }}
          mt={{ base: 2, md: 0 }}
          overflow='hidden'
          pb={{ base: 16, md: 12 }}
          position='relative'
          pt={{ base: 16, md: 12 }}
          px={{ base: 6, md: 12 }}
          style={{ backgroundImage: RESULTS_GRADIENT }}
          width='full'
        >
          <Box height='584px' mixBlendMode='overlay' position='absolute' right={0} top={0} width='160px'>
            <Image
              alt=''
              loading='lazy'
              quality={75}
              src={STRIPES_SRC}
              style={{
                objectFit: 'contain',
                objectPosition: 'right',
                pointerEvents: 'none',
                transform: 'rotate(-90deg) scale(4) translate(20px, -15px)',
              }}
              fill
            />
          </Box>

          <Flex direction='column' gap={{ base: 8, md: 12 }} position='relative'>
            <SectionHeader
              color='#7BA2FF'
              containerProps={{ maxW: '900px', mx: 'auto', width: 'full' }}
              iconSlot={<ChartUpIcon color='#7BA2FF' height='15px' width='15px' />}
              subtitle='Designed for Results'
              descriptionSlot={
                <Text
                  color='#CECFD4'
                  fontFamily='body'
                  fontSize='1rem'
                  lineHeight={1.5}
                  maxW='900px'
                  mx='auto'
                  textAlign='center'
                >
                  {RESULTS_COPY}
                </Text>
              }
              titleSlot={
                <Heading
                  as='h2'
                  color='white'
                  fontFamily='heading'
                  fontSize={{ base: '2rem', md: '3.375rem' }}
                  fontWeight={600}
                  letterSpacing='-0.02em'
                  lineHeight={1.1}
                  maxW='772px'
                  mx='auto'
                  textAlign='center'
                >
                  Udacity is on a mission to forge futures in tech.
                </Heading>
              }
            />

            <Flex flexWrap={{ base: 'wrap', md: 'nowrap' }} gap={{ base: 6, md: 6 }}>
              {RESULT_STATS.map((stat) => (
                <Box
                  key={stat.label}
                  bg='rgba(123,162,255,0.08)'
                  border='1px solid #123DA4'
                  borderRadius={{ base: '6px', md: '8px' }}
                  flex={{ base: '1 1 100%', md: '1 1 0' }}
                  minW={{ base: 'full', md: 0 }}
                  pb={{ base: 4, md: 6 }}
                  pt={{ base: 4, md: 8 }}
                  px={{ base: 4, md: 6 }}
                >
                  <Flex
                    align={{ base: 'center', md: 'stretch' }}
                    direction={{ base: 'row', md: 'column' }}
                    gap={{ base: 4, md: 0 }}
                  >
                    <Flex align='baseline' gap={1} mb={{ base: 0, md: 4 }} minW={{ base: '120px', md: 0 }}>
                      {stat.prefix && (
                        <Text
                          color={stat.valueColor}
                          fontFamily='heading'
                          fontSize={{ base: '1.75rem', md: '2rem' }}
                          fontWeight={600}
                          lineHeight={1}
                        >
                          {stat.prefix}
                        </Text>
                      )}
                      <Text
                        color={stat.valueColor}
                        fontFamily='heading'
                        fontSize={{ base: '2.625rem', md: '2.875rem' }}
                        fontWeight={600}
                        lineHeight={1}
                      >
                        {stat.value}
                      </Text>
                      {stat.suffix && (
                        <Text
                          color={stat.valueColor}
                          fontFamily='heading'
                          fontSize={{ base: '1.75rem', md: '2rem' }}
                          fontWeight={600}
                          lineHeight={1}
                        >
                          {stat.suffix}
                        </Text>
                      )}
                    </Flex>
                    <Box>
                      <Text
                        color='white'
                        fontFamily='body'
                        fontSize={{ base: '0.75rem', md: '0.8125rem' }}
                        fontWeight={500}
                        lineHeight={1.4}
                      >
                        {stat.label}
                      </Text>
                      <Text
                        color={stat.subColor}
                        fontFamily='body'
                        fontSize={{ base: '0.75rem', md: '0.8125rem' }}
                        lineHeight={1.4}
                      >
                        {stat.sub}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              ))}
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}
